
import React, { useEffect } from 'react';
import { Header } from "@/components/Header";
import { FooterSection } from "@/components/FooterSection";
import { Link } from "react-router-dom";
import AnimatedCard from "@/components/AnimatedCard";
import { FileText, Briefcase, File, Search, Shield, CreditCard, Users, Settings, ArrowRight } from "lucide-react";

const expertiseAreas = [
  {
    id: 1,
    title: "Contract Lifecycle Management",
    description: "End-to-end AI-powered solutions for contract creation, review, management, and analysis to maximize efficiency and minimize risk.",
    icon: <FileText className="h-6 w-6" />,
    color: "bg-blue-600/20",
    link: "/expertise/contract-lifecycle"
  },
  {
    id: 2,
    title: "Case Management",
    description: "AI-driven case organization, tracking, and management tools designed specifically for legal workflows and firm requirements.",
    icon: <Briefcase className="h-6 w-6" />,
    color: "bg-purple-600/20",
    link: "/expertise/case-management"
  },
  {
    id: 3,
    title: "Document Analysis & Drafting",
    description: "Advanced document review, analysis, and generation systems that leverage AI to improve accuracy and reduce preparation time.",
    icon: <File className="h-6 w-6" />,
    color: "bg-green-600/20",
    link: "/expertise/document-analysis"
  },
  {
    id: 4,
    title: "Litigation & Research",
    description: "AI tools that enhance discovery, predict outcomes, and deliver comprehensive legal research insights with unprecedented speed.",
    icon: <Search className="h-6 w-6" />,
    color: "bg-amber-600/20",
    link: "/expertise/litigation"
  },
  {
    id: 5,
    title: "Regulatory Compliance",
    description: "Automated compliance monitoring and analysis to ensure your practice stays ahead of changing regulations across jurisdictions.",
    icon: <Shield className="h-6 w-6" />,
    color: "bg-red-600/20",
    link: "/expertise/compliance"
  },
  {
    id: 6,
    title: "Commercial Transactions",
    description: "AI-enhanced strategies for managing complex commercial transactions, due diligence, and deal management workflows.",
    icon: <CreditCard className="h-6 w-6" />,
    color: "bg-indigo-600/20",
    link: "/expertise/transactions"
  },
  {
    id: 7,
    title: "Client Relationship Management",
    description: "Smarter client engagement, communication, and management systems powered by artificial intelligence.",
    icon: <Users className="h-6 w-6" />,
    color: "bg-cyan-600/20",
    link: "/expertise/client-management"
  },
  {
    id: 8,
    title: "Practice Management",
    description: "Comprehensive AI solutions for firm operations, including resource allocation, billing optimization, and workflow efficiency.",
    icon: <Settings className="h-6 w-6" />,
    color: "bg-emerald-600/20",
    link: "/expertise/practice-management"
  }
];

const Expertise = () => {
  // Split the expertise areas into three rows
  const firstRow = expertiseAreas.slice(0, 3);
  const secondRow = expertiseAreas.slice(3, 6);
  const thirdRow = expertiseAreas.slice(6, 8);

  useEffect(() => {
    document.title = "AI Legal Expertise & Practice Areas | DocketLabs";
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'AI expertise across all legal practice areas. Contract management, litigation, compliance & more. Discover our specializations.');
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = 'AI expertise across all legal practice areas. Contract management, litigation, compliance & more. Discover our specializations.';
      document.getElementsByTagName('head')[0].appendChild(meta);
    }
  }, []);
  
  return (
    <>
      <Header />
      <main className="pt-24 bg-background min-h-screen">
        <section className="px-6 py-16 relative">
          {/* Background image */}
          <div className="absolute inset-0 z-0">
            <img 
              src="/lovable-uploads/Legal_Team_at_computers.png"
              alt="Legal team at computers"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background/70 to-background/70"></div>
          </div>
          
          <div className="container mx-auto max-w-6xl relative z-10">
            <AnimatedCard animation="fade-in">
              <div className="text-center mb-12">
                <h1 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">
                  <span className="text-white">Our</span>{" "}
                  <span className="text-gradient">Expertise</span>
                </h1>
                
                <p className="text-lg md:text-xl text-foreground/80 max-w-3xl mx-auto">
                  We specialize in applying artificial intelligence across every aspect of legal practice.
                  Click below to learn how our expertise can transform your specific practice areas.
                </p>
              </div>
            </AnimatedCard>

            {/* First row - 3 cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
              {firstRow.map((area) => (
                <Link 
                  key={area.id} 
                  to={area.link}
                  className="block h-full"
                >
                  <div 
                    className="service-card h-full p-6 backdrop-blur-lg bg-white/5 border border-white/10 rounded-2xl transition-all duration-300 hover:bg-white/10 hover:shadow-lg hover:shadow-primary/10 group"
                  >
                    <div className={`p-3 rounded-xl inline-block mb-4 ${area.color}`}>
                      {area.icon}
                    </div>
                    <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                      {area.title}
                      <ArrowRight className="h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                    </h3>
                    <p className="text-white/70">{area.description}</p>
                  </div>
                </Link>
              ))}
            </div>
            
            {/* Second row - 3 cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
              {secondRow.map((area) => (
                <Link 
                  key={area.id} 
                  to={area.link}
                  className="block h-full"
                >
                  <div 
                    className="service-card h-full p-6 backdrop-blur-lg bg-white/5 border border-white/10 rounded-2xl transition-all duration-300 hover:bg-white/10 hover:shadow-lg hover:shadow-primary/10 group"
                  >
                    <div className={`p-3 rounded-xl inline-block mb-4 ${area.color}`}>
                      {area.icon}
                    </div>
                    <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                      {area.title}
                      <ArrowRight className="h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                    </h3>
                    <p className="text-white/70">{area.description}</p>
                  </div>
                </Link>
              ))}
            </div>
            
            {/* Third row - 2 cards (centered) */}
            <div className="flex justify-center gap-8">
              {thirdRow.map((area) => (
                <Link 
                  key={area.id} 
                  to={area.link}
                  className="block w-full sm:w-1/2 lg:w-1/3"
                >
                  <div 
                    className="service-card h-full p-6 backdrop-blur-lg bg-white/5 border border-white/10 rounded-2xl transition-all duration-300 hover:bg-white/10 hover:shadow-lg hover:shadow-primary/10 group"
                  >
                    <div className={`p-3 rounded-xl inline-block mb-4 ${area.color}`}>
                      {area.icon}
                    </div>
                    <h3 className="text-xl font-semibold mb-3 flex items-center gap-2">
                      {area.title}
                      <ArrowRight className="h-4 w-4 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300" />
                    </h3>
                    <p className="text-white/70">{area.description}</p>
                  </div>
                </Link>
              ))}
            </div>
            
            <div className="mt-16 text-center">
              <Link to="/contact" className="inline-flex items-center gap-2 text-primary hover:text-primary/90 transition-colors">
                <span className="text-lg font-medium">Schedule a consultation to discuss your specific needs</span>
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
          </div>
        </section>
      </main>
      <FooterSection />
    </>
  );
};

export default Expertise;
